import React from 'react';
import {
  compareProgramWorkspaceScopeHead,
  PROGRAM_WORKSPACE_SCOPE_STALE_EVENT,
  syncProgramWorkspaceBaselineFromServerRows,
  type ProgramWorkspaceScopeStaleDetail,
} from '../utils/programWorkspaceScopeSync';
import { removeProgramWorkspaceScopeEntry } from '../utils/programEditWorkspace';

export type UseProgramWorkspaceScopeServerSyncParams = {
  scopeId: string;
  enabled?: boolean;
  serverRows?: ReadonlyArray<{ updated_at?: string | null }>;
  fetchHead: () => Promise<string>;
  onAutoRefresh: () => void;
};

export type ProgramWorkspaceScopeServerSyncState = {
  staleConflict: boolean;
  dismissStaleConflict: () => void;
  acceptServerRefresh: () => void;
};

export function useProgramWorkspaceScopeServerSync(
  params: UseProgramWorkspaceScopeServerSyncParams
): ProgramWorkspaceScopeServerSyncState {
  const { scopeId, enabled = true, serverRows, fetchHead, onAutoRefresh } = params;
  const [staleConflict, setStaleConflict] = React.useState(false);
  const fetchHeadRef = React.useRef(fetchHead);
  const onAutoRefreshRef = React.useRef(onAutoRefresh);

  React.useEffect(() => {
    fetchHeadRef.current = fetchHead;
    onAutoRefreshRef.current = onAutoRefresh;
  }, [fetchHead, onAutoRefresh]);

  React.useEffect(() => {
    if (!enabled || !scopeId.trim() || !serverRows?.length) return;
    syncProgramWorkspaceBaselineFromServerRows(scopeId, serverRows);
  }, [enabled, scopeId, serverRows]);

  const runHeadCheck = React.useCallback(async () => {
    if (!enabled || !scopeId.trim()) return;
    const result = await compareProgramWorkspaceScopeHead({
      scopeId,
      fetchHead: () => fetchHeadRef.current(),
    });
    if (result === 'auto-refresh') {
      removeProgramWorkspaceScopeEntry(scopeId);
      setStaleConflict(false);
      onAutoRefreshRef.current();
    } else if (result === 'conflict') {
      setStaleConflict(true);
    }
  }, [enabled, scopeId]);

  React.useEffect(() => {
    if (!enabled || !scopeId.trim()) return;
    const onStale = (e: Event) => {
      const detail = (e as CustomEvent<ProgramWorkspaceScopeStaleDetail>).detail;
      if (detail?.scopeId === scopeId) setStaleConflict(true);
    };
    window.addEventListener(PROGRAM_WORKSPACE_SCOPE_STALE_EVENT, onStale);
    return () => window.removeEventListener(PROGRAM_WORKSPACE_SCOPE_STALE_EVENT, onStale);
  }, [enabled, scopeId]);

  React.useEffect(() => {
    if (!enabled || !scopeId.trim()) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') void runHeadCheck();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const t = window.setTimeout(() => void runHeadCheck(), 800);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      window.clearTimeout(t);
    };
  }, [enabled, runHeadCheck, scopeId]);

  const dismissStaleConflict = React.useCallback(() => setStaleConflict(false), []);

  const acceptServerRefresh = React.useCallback(() => {
    if (!scopeId.trim()) return;
    removeProgramWorkspaceScopeEntry(scopeId);
    setStaleConflict(false);
    onAutoRefreshRef.current();
  }, [scopeId]);

  return { staleConflict, dismissStaleConflict, acceptServerRefresh };
}
